import { useMemo, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Car, LogEntry, Location, supabase } from '../lib/supabase';
import { Plus, Edit, ChevronRight, Archive, TrendingUp, TrendingDown } from 'lucide-react';
import { NoteItem } from '../components/cars/NoteItem';
import { SparePartItem } from '../components/cars/SparePartItem';
import { LogEntryItem } from '../components/cars/LogEntryItem';
import { LocationItem } from '../components/cars/LocationItem';
import { ReminderItem } from '../components/cars/ReminderItem';
import { usePreferences } from '../contexts/PreferencesContext';
import { formatCurrency } from '../lib/conversions';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { theme, styles } from '../lib/theme';
import { useTranslations } from '../i18n';
import { ImageViewer } from '../components/ui/ImageViewer';
import { PdfViewer } from '../components/ui/PdfViewer';
import { isPdfFile } from '../lib/pdfUtils';
import { calculateRepairAnalysis } from '../lib/repairAnalysis';
import { HealthCircle } from '../components/ui/HealthCircle';
import { estimateCurrentMileage, estimateMileageAtDate } from '../lib/mileageUtils';
import { formatDistance } from '../lib/conversions';


type CarDashboardProps = {
    car: Car;
    logEntries: LogEntry[];
    loading?: boolean;
    galleryImages?: Array<{
        url: string;
        thumbnail?: string;
        comment?: string;
        type?: string;
        expense_id?: string;
        spare_part_id?: string;
    }>;
    notes?: Array<{ id: string; title: string; content: string; created_at: string; updated_at?: string; imageCount?: number; partCount?: number; is_pinned?: boolean; is_public?: boolean }>;
    parts?: Array<{
        id: string;
        name: string;
        price?: number;
        created_at: string;
        part_code?: string;
        comment?: string;
        spare_part_images?: { id: string }[];
    }>;
    locations?: Location[];
    reminders?: any[];
    isPublicView?: boolean;
    isPreview?: boolean;
    onTabChange: (tab: 'expenses' | 'stats' | 'gallery' | 'spare-parts' | 'notes' | 'locations' | 'repair' | 'reminders') => void;
};

export function CarOverview({ car, logEntries, loading, galleryImages = [], notes = [], parts = [], locations = [], reminders = [], isPublicView, isPreview, onTabChange }: CarDashboardProps) {
    const queryClient = useQueryClient();
    const { preferences } = usePreferences();
    const { t } = useTranslations();
    const navigate = useNavigate();
    const { slug } = useParams();
    const outletContext = useOutletContext<any>() || {};
    const isAdminView = outletContext.isAdminView || false;
    const ownerPreferences = outletContext.ownerPreferences;
    const isArchived = !!(car.is_archived);
    const canEdit = isAdminView || (!isPublicView && !isArchived);

    const effectivePreferences = (ownerPreferences || preferences) ? {
        ...(ownerPreferences || preferences),
        distance_unit: car.distance_unit || (ownerPreferences || preferences).distance_unit,
        volume_unit: car.volume_unit || (ownerPreferences || preferences).volume_unit,
        currency: car.currency || (ownerPreferences || preferences).currency,
        currency_symbol: car.currency_symbol || (ownerPreferences || preferences).currency_symbol
    } : null;

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [pdfViewerProps, setPdfViewerProps] = useState<{ url: string; link?: { url: string; label: string } } | null>(null);

    // Smart Refresh: Refresh dashboard when a reminder is supposed to trigger
    useEffect(() => {
        if (!reminders || reminders.length === 0 || isPublicView || isArchived) return;

        const now = new Date();
        let nextTrigger: number | null = null;

        reminders.forEach(r => {
            if (r.action_required || !r.is_active || r.is_seen) return;
            
            try {
                // Parse date safely avoiding UTC shift
                const timeStr = r.remind_time || '09:00';
                const triggerDate = new Date(`${r.remind_date}T${timeStr}`);
                
                if (r.last_remind_sequence > 0) {
                  triggerDate.setDate(triggerDate.getDate() + r.last_remind_sequence);
                }
                
                const diff = triggerDate.getTime() - now.getTime();
                
                // If it triggers in the future (within next 24h)
                if (diff > 0 && diff < 86400000) {
                    if (nextTrigger === null || diff < nextTrigger) {
                        nextTrigger = diff;
                    }
                }
            } catch (e) {
                console.error('Error calculating next trigger', e);
            }
        });

        if (nextTrigger !== null) {
            // Buffer: The worker runs every 5 minutes. 
            // We wait exactly until the reminder time + a safety buffer (e.g. 30 seconds)
            // to allow the worker to finish processing the RPC.
            const INITIAL_BUFFER = 30000; 
            const timer = setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['reminders', car.id] });
                
                // Safety Guard: Refresh again 30s later just in case worker was a bit slow
                setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: ['reminders', car.id] });
                }, 30000);

            }, nextTrigger + INITIAL_BUFFER);

            return () => clearTimeout(timer);
        }
    }, [reminders, car.id, queryClient, isPublicView, isArchived]);


    const thisYearStats = useMemo(() => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const prevYear = currentYear - 1;

        const sumForYear = (year: number) => logEntries
            .filter(e => new Date(e.date).getFullYear() === year)
            .reduce((sum, entry) =>
                sum + (entry.expenses || []).reduce((s, e) => s + (e.type !== 'info' ? (e.amount || 0) : 0), 0)
            , 0);

        const total = sumForYear(currentYear);
        const prevTotal = sumForYear(prevYear);
        const count = logEntries.filter(e => new Date(e.date).getFullYear() === currentYear).length;
        const trend = prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : null;

        return { total, count, trend, prevTotal };
    }, [logEntries]);

    const estimatedMileage = useMemo(() => {
        return estimateCurrentMileage(car, logEntries);
    }, [car, logEntries]);

    const recentExpenses = useMemo(() => logEntries.slice(0, 5), [logEntries]);
    const recentGallery = useMemo(() => galleryImages.slice(0, 10), [galleryImages]);
    const recentNotes = useMemo(() => notes.slice(0, 3), [notes]);
    const recentParts = useMemo(() => parts.slice(0, 3), [parts]);
    const recentLocations = useMemo(() => locations.slice(0, 3), [locations]);
    const recentReminders = useMemo(() => {
        return [...reminders]
            .sort((a, b) => {
                // Priority 1: Triggered reminders first
                if (a.action_required && !b.action_required) return -1;
                if (!a.action_required && b.action_required) return 1;

                // Priority 2: remind_date ascending
                const dateA = new Date(a.remind_date).getTime();
                const dateB = new Date(b.remind_date).getTime();
                if (dateA !== dateB) return dateA - dateB;

                // Priority 3: created_at descending
                return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
            }).slice(0, 5);
    }, [reminders]);

    const actionRequiredReminders = useMemo(() => {
        return reminders.filter(r => r.action_required);
    }, [reminders]);

    const handleAcknowledgeReminder = async (e: React.MouseEvent, id: string) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const { error } = await supabase.rpc('acknowledge_reminder', { p_reminder_id: id });
            if (error) throw error;
            // Invalidate queries to refresh UI
            queryClient.invalidateQueries({ queryKey: ['reminders'] });
            queryClient.invalidateQueries({ queryKey: ['cars'] });
        } catch (error) {
            console.error('Failed to acknowledge reminder', error);
        }
    };

    // Use Car Health Analysis logic
    const healthAnalysis = useMemo(() => {
        const p = car.repair_diagnosis_params_json || {};
        return calculateRepairAnalysis(car, logEntries, {
            plannedRepairCost: p.plannedCost,
            yearlyKm: p.yearlyKm,
            yearlyRepairCost: p.yearlyRepairCost,
            carValue: p.currentValue,
            q1: p.q1,
            q2: p.q2,
            q3: p.q3,
            rust: p.techRust,
            accident: p.techAccident,
            repaint: p.techRepaint,
            unresolved: p.techUnresolved,
            maintenanceDiscipline: p.maintenanceDiscipline,
            diyRepairs: p.diyRepairs
        });
    }, [car, logEntries]);


    const rollingStats = useMemo(() => {
        const now = new Date();
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setFullYear(now.getFullYear() - 1);
        const twentyFourMonthsAgo = new Date();
        twentyFourMonthsAgo.setFullYear(now.getFullYear() - 2);

        const currentWindowEntries = logEntries.filter(e => new Date(e.date) >= twelveMonthsAgo);
        const prevWindowEntries = logEntries.filter(e => {
            const date = new Date(e.date);
            return date < twelveMonthsAgo && date >= twentyFourMonthsAgo;
        });

        const sumExpenses = (entries: LogEntry[]) => entries.reduce((sum, entry) =>
            sum + (entry.expenses || []).reduce((s, e) => s + (e.type !== 'info' ? (e.amount || 0) : 0), 0)
            , 0);

        const currentTotal = sumExpenses(currentWindowEntries);
        const previousTotal = sumExpenses(prevWindowEntries);

        // Smart CPK Calculation (Rolling 12-Month)
        const estNow = estimateMileageAtDate(car.purchase_mileage, car.bought_at, logEntries, car.estimated_yearly_km, now);
        const est12m = estimateMileageAtDate(car.purchase_mileage, car.bought_at, logEntries, car.estimated_yearly_km, twelveMonthsAgo);
        const est24m = estimateMileageAtDate(car.purchase_mileage, car.bought_at, logEntries, car.estimated_yearly_km, twentyFourMonthsAgo);
        
        const dist = Math.max(0, estNow - est12m);
        const prevDist = Math.max(0, est12m - est24m);

        let rollingCpk = dist > 0 ? currentTotal / dist : 0;
        let previousCpk = prevDist > 0 ? previousTotal / prevDist : 0;

        const trend = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : null;
        const cpkTrend = previousCpk > 0 ? ((rollingCpk - previousCpk) / previousCpk) * 100 : null;

        return {
            currentTotal,
            previousTotal,
            trend,
            cpk: rollingCpk,
            cpkTrend
        };
    }, [car, logEntries]);


    const score = healthAnalysis.score;

    const handleAddExpense = () => {
        const targetSlug = car?.slug || slug;
        const prefix = isAdminView ? '/admin' : '';
        navigate(`${prefix}/${targetSlug}/expenses/new`);
    };

    const handleEditCar = () => {
        const targetSlug = car?.slug || slug;
        const prefix = isAdminView ? '/admin' : '';
        navigate(`${prefix}/${targetSlug}/edit`);
    };

    const handleSeeAllExpenses = () => {
        onTabChange('expenses');
    };

    const handleSeeAllGallery = () => {
        onTabChange('gallery');
    };

    const handleSeeAllNotes = () => {
        onTabChange('notes');
    };

    const handleSeeAllLocations = () => {
        onTabChange('locations');
    };

    const handleSeeAllParts = () => {
        onTabChange('spare-parts');
    };

    const handleSeeAllReminders = () => {
        onTabChange('reminders');
    };

    const handleSeeCarTasks = () => {
        const targetSlug = car?.slug || slug;
        const prefix = isAdminView ? '/admin' : '';
        navigate(`${prefix}/${targetSlug}/car-tasks`);
    };

    const handleAddNote = () => {
        const targetSlug = car?.slug || slug;
        const prefix = isAdminView ? '/admin' : '';
        navigate(`${prefix}/${targetSlug}/notes/new`);
    };

    const handleAddLocation = () => {
        const targetSlug = car?.slug || slug;
        const prefix = isAdminView ? '/admin' : '';
        navigate(`${prefix}/${targetSlug}/locations/new`);
    };

    const handleAddPart = () => {
        const targetSlug = car?.slug || slug;
        const prefix = isAdminView ? '/admin' : '';
        navigate(`${prefix}/${targetSlug}/parts/new`);
    };

    const handleAddReminder = () => {
        const targetSlug = car?.slug || slug;
        const prefix = isAdminView ? '/admin' : '';
        navigate(`${prefix}/${targetSlug}/reminders/new`);
    };

    const handleImageClick = (img: NonNullable<CarDashboardProps['galleryImages']>[0]) => {
        let matchUrl = '';
        let label = '';
        if (slug && img.type) {
            if (img.type === 'expense') {
                matchUrl = `/${slug}/expenses/${img.expense_id}`;
                label = (t.expenses as any).openExpense || 'Open Expense';
            } else if (img.type === 'note') {
                matchUrl = `/${slug}/notes/${(img as any).note_id}`;
                label = (t.notes as any).openNote || 'Open Note';
            } else {
                matchUrl = `/${slug}/parts/${img.spare_part_id}`;
                label = (t.spareParts as any).openPart || 'Open Part';
            }
        }

        const relatedLink = slug && img.type ? {
            url: matchUrl,
            label: label

        } : undefined;

        if (isPdfFile(img.url)) {
            setPdfViewerProps({ url: img.url, link: relatedLink });
        } else {
            const imageOnlyList = galleryImages.filter(i => !isPdfFile(i.url)).map(i => i.url);
            const idx = imageOnlyList.findIndex(url => url === img.url);
            if (idx >= 0) {
                setViewerIndex(idx);
                setViewerOpen(true);
            }
        }
    };



    if (loading && (!car || logEntries.length === 0)) {
        return (
            <div className="flex flex-col items-center justify-center py-24 font-primary">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
                <p className="text-gray-500 font-semibold italic">{t.common?.loading || 'Notiek ielāde...'}</p>
            </div>
        );
    }

    const isSurveyIncomplete = Object.keys(car.repair_diagnosis_params_json || {}).length === 0;

    // Get car image (use first gallery image or car icon only if enabled)
    const carImage = car.use_image_icon ? (car.image_url || galleryImages[0]?.url || null) : null;

    return (
        <div className="w-full min-h-screen pb-32">
            <div className="max-w-2xl mx-auto">
                {/* Car Header with Image */}
                <div className="relative mb-6 mx-4">
                    <div
                        onClick={!isPublicView ? handleEditCar : undefined}
                        className={`${theme.radius.card} overflow-hidden ${theme.colors.border} border ${theme.effects.shadow} relative ${!isPublicView ? 'cursor-pointer group' : ''}`}
                    >
                        {/* Car Image or Gradient Background */}
                        {carImage ? (
                            <div className="relative w-full aspect-[4/3] overflow-hidden">
                                <img
                                    src={carImage}
                                    alt={car.name || `${car.make} ${car.model}`}
                                    className="w-full h-full object-cover"
                                />
                                {/* Gradient Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 from-0% via-black/20 via-40% to-transparent to-70%" />
                            </div>
                        ) : (
                            <div
                                style={{
                                    background: car.placeholder_color || '#3B82F6',
                                    backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.2) 100%)'
                                }}
                                className="relative w-full aspect-[4/3]"
                            >
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 from-0% via-black/20 via-40% to-transparent to-70% z-0" />
                                <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none text-8xl drop-shadow-xl">{car.icon_text || '🚗'}</div>
                            </div>
                        )}

                        {/* Car Info Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white flex items-end justify-between z-20 pointer-events-none">
                            <div className="pointer-events-auto">
                                <div className="flex items-center gap-3 mb-2">
                                    {car.brand_logo_url && (
                                        <img
                                            src={car.brand_logo_url}
                                            alt={car.make}
                                            className="w-8 h-8 object-contain bg-white/90 rounded-lg p-1"
                                        />
                                    )}
                                    <h1 className="text-lg md:text-2xl font-black">
                                        {car.name || `${car.make} ${car.model}`}
                                    </h1>
                                    {isArchived && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/80 text-white text-xs font-bold">
                                            <Archive size={11} />
                                            {(t.cars as any).archivedBadge || 'Archived'}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs md:text-sm font-medium text-white/80">
                                    {car.year} • {car.make} {car.model} {car.engine_info && `• ${car.engine_info}`}
                                </p>
                                {estimatedMileage && effectivePreferences && (
                                    <p className="text-[10px] md:text-xs font-bold text-white/70 mt-1 italic">
                                        {t.cars.estimatedMileage || 'Estimated mileage'}: {formatDistance(estimatedMileage, effectivePreferences.distance_unit)}
                                    </p>
                                )}
                            </div>

                            {!isPublicView && (
                                <button
                                    onClick={handleEditCar}
                                    className="mb-1 p-2.5 bg-blue-600 dark:bg-blue-500 rounded-full border-2 border-blue-400/30 shadow-lg shadow-blue-500/20 hover:bg-blue-700 dark:hover:bg-blue-400 transition-all active:scale-95 flex items-center justify-center pointer-events-auto"
                                    aria-label={t.dashboard.editCar}
                                >
                                    <Edit size={20} strokeWidth={2.5} className="text-white" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* KPI Row Upgraded */}
                <div className="px-4 mb-6">
                    <div className="grid grid-cols-2 gap-3">
                        {/* Health Widget */}
                        <button
                            onClick={() => onTabChange('repair')}
                            className={`${styles.card} pt-3 pb-3 pr-3 pl-5 text-left active:scale-[0.98] transition-all group flex flex-col justify-between relative overflow-hidden cursor-pointer h-full min-h-[110px] gap-2`}
                        >
                            <div className="flex items-center gap-3 w-full relative">
                                <div className="relative shrink-0">
                                    <HealthCircle 
                                        score={score} 
                                        size={64} 
                                        strokeWidth={7} 
                                        innerRadius={22} 
                                        showText={true} 
                                        fontSize="15px"
                                    />
                                    {isSurveyIncomplete && (
                                        <div className="absolute top-0 -right-1 flex h-4 w-4">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-4 w-4 bg-blue-500 border-2 border-white dark:border-slate-800"></span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 py-1">
                                    <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest leading-[1.1] break-words">
                                        {(t.tabs as any).repair || 'Auto veselība'}
                                    </p>
                                </div>
                                {!isPreview && (
                                    <ChevronRight className="text-gray-300 dark:text-gray-600 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors shrink-0" size={16} strokeWidth={3} />
                                )}
                            </div>
                            <div className="mt-auto">
                                <p className="text-[10px] font-bold text-gray-800 dark:text-gray-200 leading-tight line-clamp-2 italic opacity-80">
                                    {isSurveyIncomplete ? (t.cars as any).healthProfilePrompt : healthAnalysis.recommendation}
                                </p>
                            </div>
                        </button>

                        {/* Spending Trend Widget */}
                        {rollingStats.trend !== null && (
                            <div 
                                onClick={() => onTabChange('stats')}
                                className={`${styles.card} pt-3 pb-3 pr-3 pl-5 flex flex-col justify-between cursor-pointer active:scale-[0.98] transition-all`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest leading-none">
                                        {(t.statistics as any).spendingTrend || 'Trend'}
                                    </p>
                                    {rollingStats.trend > 0 ? <TrendingUp size={14} className="text-red-500" /> : <TrendingDown size={14} className="text-green-500" />}
                                </div>
                                <div className="mt-auto">
                                    <div className="flex items-baseline gap-1.5 flex-wrap">
                                        <p className={`text-2xl font-black leading-none ${rollingStats.trend > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                            {rollingStats.trend > 0 ? '+' : ''}{rollingStats.trend.toFixed(0)}%
                                        </p>
                                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-0.5 whitespace-nowrap">{(t.statistics as any).vsPrev12m || 'vs prev 12m'}</span>
                                    </div>
                                    <div className="flex items-baseline justify-between mt-1 pt-1 border-t border-gray-50 dark:border-white/5">
                                        <p className="text-xs font-black text-gray-900 dark:text-white truncate">
                                            {effectivePreferences ? formatCurrency(rollingStats.currentTotal, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${rollingStats.currentTotal.toFixed(0)}`}
                                        </p>
                                        <span className="text-[9px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tighter">{(t.statistics as any).rolling12m || 'Rolling 12m'}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Cost Per Distance Widget */}
                        {rollingStats.cpk >= 0.01 && (
                            <div 
                                onClick={() => onTabChange('stats')}
                                className={`${styles.card} pt-3 pb-3 pr-3 pl-5 flex flex-col justify-between cursor-pointer active:scale-[0.98] transition-all`}
                            >
                                <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest leading-none mb-2">
                                    {(effectivePreferences?.distance_unit === 'miles' 
                                        ? ((t.statistics as any).costPerMileTrend || 'Cost per mile')
                                        : ((t.statistics as any).costPerKmTrend || 'Cost per 1 km'))
                                        .replace('{unit}', effectivePreferences?.distance_unit === 'miles' ? t.common.milesShort : t.common.kmShort)}
                                </p>
                                <div className="mt-auto">
                                    <div className="flex items-baseline gap-1 min-h-[24px]">
                                        <p className="text-xl font-black text-gray-900 dark:text-white leading-none">
                                            {effectivePreferences ? formatCurrency(rollingStats.cpk, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${rollingStats.cpk.toFixed(2)}`}
                                        </p>
                                        {rollingStats.cpkTrend !== null && (
                                            <span className={`text-[10px] font-bold ${rollingStats.cpkTrend > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                                {rollingStats.cpkTrend > 0 ? '+' : ''}{rollingStats.cpkTrend.toFixed(0)}%
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 uppercase font-bold tracking-wider truncate">
                                        {t.statistics.rolling12m}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* This Year Total Widget */}
                        <div 
                             onClick={() => onTabChange('stats')}
                             className={`${styles.card} pt-3 pb-3 pr-3 pl-5 flex flex-col justify-between cursor-pointer active:scale-[0.98] transition-all`}
                        >
                            <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest leading-none mb-2">{(t.statistics as any).spentIn?.replace('{year}', new Date().getFullYear().toString()) || new Date().getFullYear().toString()}</p>
                            <div className="mt-auto">
                                <div className="flex items-baseline gap-1 min-h-[24px]">
                                    <p className="text-xl font-black text-gray-900 dark:text-white leading-none">
                                        {effectivePreferences ? formatCurrency(thisYearStats.total, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${thisYearStats.total.toFixed(0)}`}
                                    </p>
                                    {thisYearStats.trend !== null && (
                                        <span className={`text-[10px] font-bold ${thisYearStats.trend > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                            {thisYearStats.trend > 0 ? '+' : ''}{thisYearStats.trend.toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 uppercase font-bold tracking-wider truncate">
                                    {thisYearStats.count} {t.tabs.expenses.toLowerCase()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Required Reminders - Standalone Pulsating Items */}
                {actionRequiredReminders.map(reminder => (
                    <div key={reminder.id} className="px-4 mb-3">
                        <ReminderItem
                            reminder={reminder}
                            slug={slug || car.slug || ''}
                            isAdminView={isAdminView}
                            className="border-2 border-blue-500 bg-blue-50/50 dark:bg-blue-500/10 shadow-[0_0_20px_rgba(59,130,246,0.4)] animate-remind-pulse"
                            showCheckmark={true}
                            onCheck={(e) => handleAcknowledgeReminder(e, reminder.id)}
                        />
                    </div>
                ))}

                {/* Recent Expenses */}
                <div className="px-4 mb-6">
                    <div className="flex items-center justify-between mb-3 px-1">
                        <button
                            onClick={handleSeeAllExpenses}
                            className="flex-1 flex items-center justify-start gap-2 group transition-all active:scale-[0.98] mr-2"
                        >
                            <h2 className={theme.typography.sectionTitle}>
                                {t.dashboard.recentExpenses} {logEntries.length > 0 && <span className="text-blue-600 dark:text-blue-400 ml-1.5 font-medium text-[0.85em]">({logEntries.length})</span>}
                            </h2>
                            <ChevronRight size={20} className="text-blue-600 dark:text-blue-400 mt-0" strokeWidth={2.5} />
                        </button>
                        {canEdit && (
                            <button
                                onClick={handleAddExpense}
                                className="bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm flex items-center justify-center"
                            >
                                <Plus size={22} strokeWidth={2.5} />
                            </button>
                        )}
                    </div>

                    {recentExpenses.length === 0 ? (
                        <div className={`${styles.card} p-8 text-center`}>
                            {canEdit ? (
                                <button
                                    onClick={handleAddExpense}
                                    className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold rounded-lg text-sm hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                >
                                    {t.dashboard.createExpense}
                                </button>
                            ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-sm italic">{t.dashboard.noRecentExpenses || 'Nav ierakstu'}</span>
                            )}
                        </div>
                    ) : (
                        <div className={styles.card}>
                            <div className="divide-y divide-gray-100 dark:divide-white/5">
                                {recentExpenses.map((expense) => (
                                    <LogEntryItem
                                        key={expense.id}
                                        entry={expense}
                                        slug={slug || car.slug || ''}
                                        isAdminView={isAdminView}
                                        isPublicView={isPublicView}
                                        shareLocationsEnabled={car.share_locations}
                                        effectivePreferences={effectivePreferences}
                                    />
                                ))}

                            </div>
                        </div>
                    )}
                </div>

                {/* Recent Notes & Parts Grid */}
                <div className="px-4 mb-6">
                    <div className="grid grid-cols-1 gap-6">


                        {/* Recent Notes */}
                        <div>
                            <div className="flex items-center justify-between mb-3 px-1">
                                <button
                                    onClick={handleSeeAllNotes}
                                    className="flex-1 flex items-center justify-start gap-2 group transition-all active:scale-[0.98] mr-2"
                                >
                                    <h2 className={theme.typography.sectionTitle}>
                                        {t.dashboard.recentNotes} {notes.length > 0 && <span className="text-blue-600 dark:text-blue-400 ml-1.5 font-medium text-[0.85em]">({notes.length})</span>}
                                    </h2>
                                    <ChevronRight size={20} className="text-blue-600 dark:text-blue-400 mt-0" strokeWidth={2.5} />
                                </button>
                                {canEdit && (
                                    <button
                                        onClick={handleAddNote}
                                        className="bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm flex items-center justify-center"
                                    >
                                        <Plus size={22} strokeWidth={2.5} />
                                    </button>
                                )}
                            </div>

                            {recentNotes.length === 0 ? (
                                <div className={`${styles.card} p-8 text-center`}>
                                    {canEdit ? (
                                        <button
                                            onClick={handleAddNote}
                                            className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold rounded-lg text-sm hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                        >
                                            {t.dashboard.createNote}
                                        </button>
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500 text-sm italic">{t.dashboard.noRecentNotes || 'Nav piezīmju'}</span>
                                    )}
                                </div>
                            ) : (
                                <div className={styles.card}>
                                    <div className="divide-y divide-gray-100 dark:divide-white/5">
                                        {recentNotes.map((note) => (
                                            <NoteItem
                                                key={note.id}
                                                note={{
                                                    ...note,
                                                    updated_at: (note as any).updated_at || note.created_at
                                                }}
                                                slug={slug}
                                                isAdminView={isAdminView}
                                                compact={true}
                                                carIsPublic={car.is_public}
                                            />
                                        ))}

                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Recent Parts */}
                        <div>
                            <div className="flex items-center justify-between mb-3 px-1">
                                <button
                                    onClick={handleSeeAllParts}
                                    className="flex-1 flex items-center justify-start gap-2 group transition-all active:scale-[0.98] mr-2"
                                >
                                    <h2 className={theme.typography.sectionTitle}>
                                        {t.dashboard.recentParts} {parts.length > 0 && <span className="text-blue-600 dark:text-blue-400 ml-1.5 font-medium text-[0.85em]">({parts.length})</span>}
                                    </h2>
                                    <ChevronRight size={20} className="text-blue-600 dark:text-blue-400 mt-0" strokeWidth={2.5} />
                                </button>
                                {canEdit && (
                                    <button
                                        onClick={handleAddPart}
                                        className="bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm flex items-center justify-center"
                                    >
                                        <Plus size={22} strokeWidth={2.5} />
                                    </button>
                                )}
                            </div>

                            {recentParts.length === 0 ? (
                                <div className={`${styles.card} p-8 text-center`}>
                                    {canEdit ? (
                                        <button
                                            onClick={handleAddPart}
                                            className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold rounded-lg text-sm hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                        >
                                            {t.dashboard.createPart}
                                        </button>
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500 text-sm italic">{t.dashboard.noRecentParts || 'Nav daļu'}</span>
                                    )}
                                </div>
                            ) : (
                                <div className={styles.card}>
                                    <div className="divide-y divide-gray-100 dark:divide-white/5">
                                        {recentParts.map((part) => (
                                            <SparePartItem
                                                key={part.id}
                                                part={part}
                                                slug={slug}
                                                isAdminView={isAdminView}
                                                compact={true}
                                            />
                                        ))}

                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Reminders Section */}
                        <div>
                            <div className="flex items-center justify-between mb-3 px-1">
                                <button
                                    onClick={handleSeeAllReminders}
                                    className="flex-1 flex items-center justify-start gap-2 group transition-all active:scale-[0.98] mr-2"
                                >
                                    <h2 className={theme.typography.sectionTitle}>
                                        {t.reminders.title} {reminders.length > 0 && <span className="text-blue-600 dark:text-blue-400 ml-1.5 font-medium text-[0.85em]">({reminders.length})</span>}
                                    </h2>
                                    <ChevronRight size={20} className="text-blue-600 dark:text-blue-400 mt-0" strokeWidth={2.5} />
                                </button>
                                {canEdit && (
                                    <button
                                        onClick={handleAddReminder}
                                        className="bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm flex items-center justify-center"
                                    >
                                        <Plus size={22} strokeWidth={2.5} />
                                    </button>
                                )}
                            </div>

                            {recentReminders.length === 0 ? (
                                <div className={`${styles.card} p-8 text-center`}>
                                    {canEdit ? (
                                        <button
                                            onClick={handleAddReminder}
                                            className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold rounded-lg text-sm hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                        >
                                            {t.reminders.addReminder}
                                        </button>
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500 text-sm italic">{t.reminders.noReminders}</span>
                                    )}
                                </div>
                            ) : (
                                <div className={styles.card}>
                                    <div className="divide-y divide-gray-100 dark:divide-white/5">
                                        {recentReminders.map((reminder) => (
                                            <ReminderItem
                                                key={reminder.id}
                                                reminder={reminder}
                                                compact={true}
                                                onClick={() => {
                                                    const targetSlug = car?.slug || slug;
                                                    const prefix = isAdminView ? '/admin' : '';
                                                    if (reminder.note_id) {
                                                        navigate(`${prefix}/${targetSlug}/notes/${reminder.note_id}`);
                                                    } else if (reminder.part_id) {
                                                        navigate(`${prefix}/${targetSlug}/parts/${reminder.part_id}`);
                                                    } else {
                                                        navigate(`${prefix}/${targetSlug}/reminders/${reminder.id}`);
                                                    }
                                                }}
                                            />
                                        ))}

                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Recent Locations */}
                        {(!isPublicView || car.share_locations) && (
                            <div>
                                <div className="flex items-center justify-between mb-3 px-1">
                                    <button
                                        onClick={handleSeeAllLocations}
                                        className="flex-1 flex items-center justify-start gap-2 group transition-all active:scale-[0.98] mr-2"
                                    >
                                        <h2 className={theme.typography.sectionTitle}>
                                            {t.locations.myLocations} {locations.length > 0 && <span className="text-blue-600 dark:text-blue-400 ml-1.5 font-medium text-[0.85em]">({locations.length})</span>}
                                        </h2>
                                        <ChevronRight size={20} className="text-blue-600 dark:text-blue-400 mt-0" strokeWidth={2.5} />
                                    </button>
                                    {canEdit && (
                                        <button
                                            onClick={handleAddLocation}
                                            className="bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm flex items-center justify-center"
                                        >
                                            <Plus size={22} strokeWidth={2.5} />
                                        </button>
                                    )}
                                </div>

                                {recentLocations.length === 0 ? (
                                    <div className={`${styles.card} p-8 text-center`}>
                                        {canEdit ? (
                                            <button
                                                onClick={handleAddLocation}
                                                className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold rounded-lg text-sm hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                            >
                                                {t.locations.createNew}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 dark:text-gray-500 text-sm italic">{t.locations.noLocations}</span>
                                        )}
                                    </div>
                                ) : (
                                    <div className={styles.card}>
                                        <div className="divide-y divide-gray-100 dark:divide-white/5">
                                            {recentLocations.map((loc) => (
                                                <LocationItem
                                                    key={loc.id}
                                                    location={loc}
                                                    slug={slug}
                                                    isAdminView={isAdminView}
                                                    compact={true}
                                                    onClick={() => {
                                                        const targetSlug = car?.slug || slug;
                                                        const prefix = isAdminView ? '/admin' : '';
                                                        navigate(`${prefix}/${targetSlug}/locations/${loc.id}`);
                                                    }}
                                                />
                                            ))}

                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent Gallery */}
                {recentGallery.length > 0 && (
                    <div className="px-4 mb-6">
                        <div className="flex items-center justify-between mb-3 px-1">
                            <button
                                onClick={handleSeeAllGallery}
                                className="flex-1 flex items-center justify-start gap-2 group transition-all active:scale-[0.98] mr-2"
                            >
                                <h2 className={theme.typography.sectionTitle}>
                                    {t.dashboard.recentGallery} {galleryImages.length > 0 && <span className="text-blue-600 dark:text-blue-400 ml-1.5 font-medium text-[0.85em]">({galleryImages.length})</span>}
                                </h2>
                                <ChevronRight size={20} className="text-blue-600 dark:text-blue-400 mt-0" strokeWidth={2.5} />
                            </button>
                        </div>

                        <div className="flex gap-3 overflow-x-auto pb-4 no-scrollbar">
                            {recentGallery.map((img, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handleImageClick(img)}
                                    className="flex-shrink-0 w-28 group"
                                >
                                    <div className="w-28 h-28 rounded-2xl overflow-hidden border border-gray-100 dark:border-white/10 group-hover:scale-105 transition-transform active:scale-95 relative bg-gray-50 dark:bg-white/5">
                                        <img
                                            src={img.thumbnail || img.url}
                                            alt={`Gallery ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                        {img.comment && (
                                            <>
                                                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
                                                <p className="absolute bottom-2 inset-x-0 text-[10px] leading-[1.1] text-white font-semibold w-full text-center px-2 line-clamp-2 drop-shadow-md">
                                                    {img.comment}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Car Tasks (Fake Door) - Hidden until requested back
                <div className="px-4 mb-10">
                    <div className="flex items-center justify-between mb-3 px-1">
                        <button
                            onClick={handleSeeCarTasks}
                            className="flex-1 flex items-center justify-start gap-2 group transition-all active:scale-[0.98] mr-2"
                        >
                            <h2 className={theme.typography.sectionTitle}>
                                {t.carTasks.title}
                            </h2>
                            <ChevronRight size={20} className="text-blue-600 dark:text-blue-400 mt-0" strokeWidth={2.5} />
                        </button>
                        {canEdit && (
                            <button
                                onClick={handleSeeCarTasks}
                                className="bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm flex items-center justify-center"
                            >
                                <Plus size={22} strokeWidth={2.5} />
                            </button>
                        )}
                    </div>

                    <button
                        onClick={handleSeeCarTasks}
                        className={`${styles.card} w-full p-8 text-center group active:scale-[0.99] transition-all border-dashed border-2 border-gray-200 dark:border-white/10 bg-transparent hover:bg-gray-50 dark:hover:bg-white/5`}
                    >
                        <div className="flex flex-col items-center gap-3">
                            <h3 className="font-bold text-gray-900 dark:text-white">
                                {t.carTasks.placeholderTitle}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-[240px] mx-auto leading-relaxed">
                                {t.carTasks.placeholderDesc}
                            </p>
                        </div>
                    </button>
                </div>
                */}

                {/* Feedback Link */}
                <div className="mt-12 mb-8 text-center pb-8 border-t border-gray-100 dark:border-white/5 pt-8">
                    <p className="text-sm font-medium text-gray-400">
                        {t.dashboard.needSomethingMore}{' '}
                        <button 
                            onClick={() => navigate('/contact')} 
                            className="text-blue-500 hover:text-blue-600 transition-colors font-bold"
                        >
                            {t.dashboard.letUsKnow}
                        </button>
                    </p>
                </div>
            </div>
            {viewerOpen && (
                <ImageViewer
                    images={galleryImages.filter(img => !isPdfFile(img.url)).map(img => img.url)}
                    initialIndex={viewerIndex}
                    onClose={() => setViewerOpen(false)}
                    relatedLinks={galleryImages.filter(img => !isPdfFile(img.url)).map(img => {
                        if (!slug || !img.type) return undefined;
                        let matchUrl = '';
                        let label = '';
                        if (img.type === 'expense') {
                            matchUrl = `/${slug}/expenses/${img.expense_id}`;
                            label = (t.expenses as any).openExpense || 'Open Expense';
                        } else if (img.type === 'note') {
                            matchUrl = `/${slug}/notes/${(img as any).note_id}`;
                            label = (t.notes as any).openNote || 'Open Note';
                        } else {
                            matchUrl = `/${slug}/parts/${img.spare_part_id}`;
                            label = (t.spareParts as any).openPart || 'Open Part';
                        }
                        return { url: matchUrl, label };
                    })}
                />
            )}
            {pdfViewerProps && (
                <PdfViewer
                    url={pdfViewerProps.url}
                    onClose={() => setPdfViewerProps(null)}
                    relatedLink={pdfViewerProps.link}
                />
            )}
        </div>
    );
}
